import React, { useContext, useState } from "react";
import { observer } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { Toolbar } from "primereact/toolbar";
import ToastContext from "../../components/toast/ToastContext";
import ScenarioStore from "../../store/ScenarioStore";
import { ScenarioToasts } from "../../toastMessages/scenarioToastMessages";
import EmergencyOptionSelection from "./formFields/EmergencyOptionSelection";
import ConfirmDialog from "../../components/dialogs/ConfirmDialog";
import { Scenario } from "../../api/firebase/types/scenario.types";
import UserDataStore from "../../store/UserDataStore";
import SaveCancelFooter from "../../components/footer/SaveCancelFooter";
import { useFormik } from "formik";
import { EmergencyCall } from "../../api/firebase/types/incident.types";
import IncidentDetailsField from "./formFields/IncidentDetailsField";
import TitleField from "./formFields/TitleField";

export type ScenarioCreationModalProps = {
  scenario?: Scenario;
  isDialogVisible: boolean;
  closeDialog: () => void;
};

type ScenarioCreationFormData = Pick<Scenario, "title" | "incidentDetails"> & {
  emergencyCall?: EmergencyCall;
};

const ScenarioCreationModal: React.FC<ScenarioCreationModalProps> = (props) => {
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);

  const { emergencyOptions } = UserDataStore.currentUser!;

  const formik = useFormik<ScenarioCreationFormData>({
    initialValues: {
      title: "",
      emergencyCall:
        emergencyOptions.length === 1 ? emergencyOptions[0] : undefined, // Wenn es keine Auswahl gibt, wird standardmäßig das einzige verwendet
      incidentDetails: "",
    },
    validate: (data) => {
      let errors: Partial<Record<keyof ScenarioCreationFormData, string>> = {};

      if (!data.title) {
        errors.title = "Bitte geben Sie dem Szenario eine Bezeichnung";
      } else if (data.title.length < 2) {
        errors.title = "Die Bezeichnung ist zu kurz";
      }

      if (!data.emergencyCall && emergencyOptions.length > 1) {
        errors.emergencyCall = "Bitte geben Sie einen Notruf-Typen an";
      }

      return errors;
    },
    onSubmit: (data: any) => {
      setShowLoader(true);
      const newScenario: Scenario = {
        ...data,
        incidentDetails: data.incidentDetails || "",
      };

      ScenarioStore.addScenario(newScenario)
        .then(() => {
          showToast(ScenarioToasts.scenarioCreated);
        })
        .catch(() => {
          showToast(ScenarioToasts.scenarioCreationError);
        })
        .finally(() => {
          setShowLoader(false);
          props.closeDialog();
        });
    },
  });

  const showToast = useContext(ToastContext);

  const onCancel = () => {
    formik.resetForm();
    props.closeDialog();
  };

  const handleCancelClick = () => {
    setCancelDialogVisible(true);
  };

  const isFormFieldValid = (name: keyof ScenarioCreationFormData) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getFormErrorMessage = (name: keyof ScenarioCreationFormData) =>
    isFormFieldValid(name) ? (
      <small className="p-error p-d-block form-error">
        {formik.errors[name]}
      </small>
    ) : undefined;

  return (
    <Dialog
      visible={props.isDialogVisible}
      header={<Toolbar left={() => <b>Szenario erstellen</b>} />}
      modal
      className="p-fluid device-detail p-col"
      footer={
        <SaveCancelFooter
          submitAction={formik.handleSubmit}
          cancelAction={handleCancelClick}
        />
      }
      onHide={handleCancelClick}
    >
      {showLoader && <ProgressBar mode="indeterminate" />}
      <div className="device-setting-section">
        <TitleField
          value={formik.values.title}
          onChange={formik.handleChange}
        />
        {getFormErrorMessage("title")}
        <EmergencyOptionSelection
          selectedEmergencyCall={formik.values.emergencyCall}
          emergencyOptions={emergencyOptions}
          onChange={(emergencyCall: EmergencyCall) =>
            formik.setFieldValue("emergencyCall", emergencyCall)
          }
        />
        {getFormErrorMessage("emergencyCall")}
        <IncidentDetailsField
          value={formik.values.incidentDetails}
          onChange={formik.handleChange}
        />
      </div>
      <ConfirmDialog
        header="Szenario-Erstellung abbrechen?"
        content="Alle Änderungen gehen dabei verloren"
        isVisible={cancelDialogVisible}
        onHide={() => setCancelDialogVisible(false)}
        onClickYes={onCancel}
      />
    </Dialog>
  );
};

export default observer(ScenarioCreationModal);
